import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Home, Trophy, Calendar, ListOrdered, Settings, Menu, X, LogOut, Cog, Coins, Wallet, LifeBuoy } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { User } from 'lucide-react';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { useUserProfile } from '@/hooks/useUserProfile';
import { useBetaCredits } from '@/hooks/useBetaCredits';
import { useSportSwitcher } from '@/hooks/useSportSwitcher';
import AccountSettingsDialog from '@/components/AccountSettingsDialog';
import { openSupport } from '@/lib/freshdesk';

interface MobileHamburgerMenuProps {
  activeTab: string;
  onTabChange: (tab: string) => void;
}

const menuItems = [
  { id: 'home', label: 'Home', icon: Home },
  { id: 'standings', label: 'Standings', icon: Trophy },
  { id: 'fantasy-schedule', label: 'Schedule', icon: Calendar },
  { id: 'draft', label: 'Draft', icon: ListOrdered },
  { id: 'settings', label: 'League Settings', icon: Settings },
  // SAB-958 / SAB-411: the Worst Ball row is gone. It was a dead link on NFL, and the
  // feature is deferred to next season. Removed here only — DesktopSidebar and RedditDemo
  // still carry it, which is deliberate scope, not an oversight.
];

const MobileHamburgerMenu = ({ activeTab, onTabChange }: MobileHamburgerMenuProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const { signOut } = useAuth();
  const profile = useUserProfile();
  const betaCredits = useBetaCredits();

  const navigate = useNavigate();

  // SAB-409 / SAB-576: the Switch Sport row shows the INVERSE of the current sport
  // (route-detected) and routes to its entry. Always rendered now — membership decides the
  // destination (their league vs the /nfl/start chooser) and the label, not visibility; the
  // old membership gate hid the only in-app path to a sport you had not joined yet. Shared with
  // the desktop switcher in UserAccountMenu via useSportSwitcher (one source of truth). The
  // target page owns league selection (NFLNav's LeagueSwitcher / MLBBeta).
  const { target: switchTarget, destination: switchTo, actionLabel: switchLabel } = useSportSwitcher();

  const handleNavigate = (tab: string) => {
    onTabChange(tab);
    setIsOpen(false);
  };

  const handleOpenAccountSettings = () => {
    setIsOpen(false);
    profile.loadProfile();
    setEditOpen(true);
  };

  const drawerContent = (
    <>
      {/* Backdrop overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 z-[60] bg-black/60"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Slide-in drawer from right.
          flex-col so nav can take flex-1 + scroll, and Sign Out sits in natural
          flow as the last child — replaces the previous absolute bottom-8 Sign Out
          which crowded the NFL row when nav content was tall on shorter phones. */}
      <div
        className={cn(
          "fixed top-0 right-0 z-[70] h-full w-[75vw] max-w-xs border-l border-border shadow-2xl transition-transform duration-300 ease-in-out flex flex-col",
          isOpen ? "translate-x-0" : "translate-x-full"
        )}
        style={{ backgroundColor: '#0f1724' }}
      >
        {/* Header — respects safe area */}
        <div
          className="flex items-center justify-between px-4 pb-4 border-b border-border"
          style={{ paddingTop: 'max(1rem, env(safe-area-inset-top))' }}
        >
          <span className="font-display font-bold text-lg text-foreground">Menu</span>
          <button
            onClick={() => setIsOpen(false)}
            className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            aria-label="Close menu"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* User account section */}
        <button
          onClick={handleOpenAccountSettings}
          className="w-full flex items-center gap-3 px-4 py-4 border-b border-border text-left hover:bg-muted/50 transition-colors"
        >
          <Avatar className="h-10 w-10 shrink-0 border border-border">
            <AvatarImage src={profile.avatarUrl || undefined} alt="Avatar" />
            <AvatarFallback className="text-xs">
              {profile.avatarUrl ? profile.initials : <User className="h-5 w-5 text-muted-foreground" />}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            {profile.displayName && (
              <p className="text-sm font-semibold text-foreground truncate">{profile.displayName}</p>
            )}
            <p className="text-xs text-muted-foreground truncate">{profile.user?.email}</p>
          </div>
        </button>

        {/* Beta Credits */}
        {betaCredits.isBetaUser && betaCredits.totalCredits > 0 && (
          <div className="mx-4 mt-3 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Coins className="w-4 h-4 text-amber-400" />
                <span className="text-xs font-semibold text-amber-400">Sabotage Credits</span>
              </div>
              <Badge className="bg-success/20 text-success border-success/30 text-[10px]">${betaCredits.totalCredits}</Badge>
            </div>
          </div>
        )}

        {/* Nav items — flex-1 so nav fills available drawer height, overflow-y-auto for safety on shorter phones */}
        <nav className="flex-1 overflow-y-auto p-4 space-y-1">
          {menuItems.map((item) => (
            <button
              key={item.id}
              onClick={() => handleNavigate(item.id)}
              className={cn(
                "w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all text-left",
                activeTab === item.id
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:text-foreground hover:bg-muted"
              )}
            >
              <item.icon className="w-5 h-5" />
              <span className="font-medium">{item.label}</span>
            </button>
          ))}
          {/* SAB-565 pt 1: account-level Wallet */}
          <button
            onClick={() => { navigate('/wallet'); setIsOpen(false); }}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-all text-left"
          >
            <Wallet className="w-5 h-5" />
            <span className="font-medium">Wallet</span>
          </button>
          <button
            onClick={() => { navigate('/app-settings'); setIsOpen(false); }}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-all text-left"
          >
            <Cog className="w-5 h-5" />
            <span className="font-medium">App Settings</span>
          </button>

          {/* Sport switcher — SAB-409/576: shows the sport you're NOT in and routes there.
              Always rendered; membership decides the destination + label, not visibility. */}
          <div className="border-t border-border mt-2 pt-2">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground/60 px-4 mb-1">Switch Sport</p>
            <button
              onClick={() => { navigate(switchTo); setIsOpen(false); }}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-all text-left"
            >
              <span className="text-lg">{switchTarget.emoji}</span>
              <span className="font-medium">{switchLabel}</span>
              <Badge variant="outline" className="ml-auto mr-1 text-[9px] h-4 border-orange-500/30 text-orange-400">Beta</Badge>
            </button>
          </div>
        </nav>

        {/* Sign Out — natural-flow last child (was absolute bottom-8 which overlapped NFL row on shorter phones).
            shrink-0 + border-t to keep it visually anchored to the bottom regardless of nav content height. */}
        <div className="shrink-0 border-t border-border px-4 pt-4 pb-8">
          {/*
            SAB-945: THE ONLY SUPPORT ENTRY POINT ON MOBILE.

            The floating launcher is hidden below 768px. Three attempts to position it each
            moved the problem rather than solving it -- draft room, then consent banner, then
            the bottom nav's Players tab and the hamburger -- because the bottom-right corner
            on a phone belongs to our own navigation. In here it cannot overlap anything, and
            it is where users already look for secondary actions.

            ⚠️ ALWAYS RENDERED, NEVER CONDITIONAL ON WIDGET READINESS. Gating this on
            useSupportAvailable() would make support VANISH from the menu exactly when the
            widget is unavailable -- no consent, vendor script still loading, vendor outage --
            which is when a stuck user most needs a way through. So the item is always here
            and openSupport()'s return value chooses the destination: chat if it can open,
            otherwise /support, which carries support@sabotagefantasy.com. One entry, two
            possible outcomes, never a dead end.
          */}
          <button
            onClick={() => {
              const opened = openSupport();
              if (!opened) navigate('/support');
              setIsOpen(false);
            }}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-all text-left"
          >
            <LifeBuoy className="w-5 h-5" />
            <span className="font-medium">Live Support</span>
          </button>
          <button
            onClick={() => { signOut(); setIsOpen(false); }}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-muted-foreground hover:text-destructive hover:bg-muted transition-all text-left"
          >
            <LogOut className="w-5 h-5" />
            <span className="font-medium">Sign Out</span>
          </button>
        </div>
      </div>
    </>
  );

  return (
    <div className="lg:hidden">
      {/* Hamburger trigger */}
      <button
        onClick={() => setIsOpen(true)}
        className="p-2.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors touch-manipulation"
        aria-label="Open menu"
      >
        <Menu className="w-6 h-6" />
      </button>

      {/* Portal the drawer to document.body to escape stacking contexts */}
      {createPortal(drawerContent, document.body)}

      {/* Account Settings dialog — rendered outside the drawer portal */}
      <AccountSettingsDialog open={editOpen} onOpenChange={setEditOpen} profile={profile} />
    </div>
  );
};

export default MobileHamburgerMenu;
